import { createEffect, createMemo, createSignal, on, untrack } from "solid-js";
import { token } from "./auth";
import { type ChannelKey, canonicalChannel, channelKey, decodeChannelKey } from "./channelKey";
import { encryptedChat } from "./encryptedChats";
import { identityScopedStore } from "./identityScopedStore";
import { casemappingForNetwork } from "./isupport";
import { saveLastFocused } from "./lastFocusedChannel";
import { membersByChannel } from "./members";
import { evictFromMru, pickLiveMru, recordFocus } from "./mru";
import { channelsBySlug, networkBySlug, networks, user } from "./networks";
import { nickEquals } from "./nickEquals";
import { presenceRowVisible } from "./presenceFilter";
import { queryWindowsByNetwork } from "./queryWindows";
import { getReadCursor, readCursors, setReadCursor } from "./readCursor";
import { readingAtTailKey } from "./readingAtTail";
import {
  farBehindByChannel,
  loadInitialScrollback,
  measuredUnreadByChannel,
  refreshScrollback,
  scrollbackByChannel,
  wasLoaded,
} from "./scrollback";
import { countsAsUnreadEvent, type UnreadRowContext, unreadMessagesAfter } from "./unreadCount";
import { unreadRowContextFor } from "./unreadRowContext";
import {
  encryptedWindowPeer,
  HOME_WINDOW_NAME,
  HOME_WINDOW_SLUG,
  kindHasScrollback,
  SERVER_WINDOW_NAME,
  type WindowKind,
} from "./windowKinds";
import { windowIsPresent } from "./windowState";

// Per-channel selection store: which channel is currently focused +
// per-channel unread counters. Module-singleton signal store mirroring
// `auth.ts` / `socket.ts` / `scrollback.ts`.
//
// Lifted out of the original `networks.ts` god-module per A4. Owns:
//   * `selectedChannel` — the (slug, name, kind) tuple of the focused pane.
//   * `serverSeedCounts` — the per-channel `{messages, events}` count
//     pair seeded from the server's per-channel join reply
//     (`unread_count`) and `/me` envelope (`unread_counts`). Used as a
//     fallback when local scrollback hasn't been hydrated for a
//     channel (cold start or never-opened channel).
//   * `unreadCounts` / `messagesUnread` / `eventsUnread` — DERIVED memos
//     over `(scrollbackByChannel, readCursors, serverSeedCounts)`. For
//     each known channel, the memo counts local rows with `id > cursor`
//     split by content vs presence kind. When local scrollback is empty
//     for a channel, falls back to `serverSeedCounts[key]`.
//   * Selection-change effect: fires `scrollback.loadInitialScrollback`
//     to backfill history (the load-once gate lives in scrollback.ts)
//     + clears mention counts for the focused window. As the cursor
//     advances (via read-at-the-tail, scroll-settle, focus-leave,
//     browser-blur, send, and fresh-channel load-baseline in
//     scrollback.ts) the derived counts fall on their own — in the
//     FOCUSED window exactly as in every other one (#887; the old
//     focused-window zeroing overwrite is gone, see `perChannelUnread`).
//     The one window that IS zeroed is the one the pane reports it is
//     reading at the tail (#981, `readingAtTail`) — a narrower rule than
//     the #887 one, and honest for the reason spelled out at the memo.
//
// 2026-06-01 (unread-badges-from-cursor cluster, bucket B2): the four
// increment stores (`unreadCounts`, `messagesUnread`, `eventsUnread`,
// `mentionCounts`) used to drift any time the bump-on-receive
// predicate diverged from the in-pane cursor-vs-tail predicate. Two
// bugs surfaced from this:
//   1. Cross-session own-message bump — sending on phone bumped the
//      laptop badge because the WS broadcast filter caught own
//      presence + own server-numeric echoes but NOT own content.
//   2. Marker-stuck-on-send-in-focused — sending in the focused window
//      didn't reset the in-pane `── XX unread ──` marker because the
//      cursor only advanced on focus-leave + browser-blur.
// Server-derived counts collapse both structurally: cursor advances on
// send (bucket D), broadcasts `read_cursor_set`, both devices' derived
// counts drop in unison. `mentionCounts` stays bump-based — it's a
// body-text predicate, not pure count-after-cursor — but the bump
// path now gates on own-sender too (`mentions.ts`).
//
// Identity-scoped via identityScopedStore: two resets registered — one
// for `serverSeedCounts`, one for `selectedChannel`. The derived memos
// auto-reset when their upstream signals (scrollback, cursors, seeds)
// reset on identity transition. Selection-effect arms (selection
// transition, visibility transition, network connection-state, close-
// watcher) stay inline — orthogonal to identity rotation.

export type SelectedChannel = {
  networkSlug: string;
  channelName: string;
  kind: WindowKind;
} | null;

// Exact-tuple selection equality: two selections are the same iff both are
// null, or both non-null with identical (slug, name, kind). Any change in
// slug, name, or kind — and null↔non-null — is a real transition. Single
// source of truth for BOTH the idempotent setter's short-circuit AND the
// #243 re-tap predicate (`isActiveSelection`), so the two can never
// diverge.
const sameSelection = (a: SelectedChannel, b: SelectedChannel): boolean => {
  if (a === null || b === null) return a === b;
  return a.networkSlug === b.networkSlug && a.channelName === b.channelName && a.kind === b.kind;
};

// #1396 (review bucket G, A9) — the channel KEY folds HERE, once, instead of
// in every caller. Four doors open a channel window and each spelled the fold
// itself; the one that did not (`HomePane`'s featured-link tap) was correct
// only because the server happens to canonicalise
// `network_featured_channels` at write. The store paid for that asymmetry
// twice: `sameSelection` compares byte-wise, so two spellings of one channel
// were two selections, and `stillLive` below carries a defensive
// decode-through-the-key whose comment names "a stray non-canonical
// setSelectedChannel" as its reason. Folding at the boundary is what makes
// the stray impossible rather than survivable, and it is where CLAUDE.md puts
// the fold anyway: at the KEY boundary, once.
//
// ONLY `kind: "channel"`. A channel has no display column — the folded key IS
// the display (option B) — so nothing is lost by folding on the way in. The
// other kinds must NOT fold:
//
//   * `query` — the name is a peer NICK, and its raw casing is both display
//     AND WIRE. `compose.ts`'s `resolveBareWhoisNick` hands `sel.channelName`
//     straight into a WHOIS frame, so folding here would fold a wire builder,
//     which the key/display/wire split forbids. Query focus sites already
//     fold their own KEY via `canonicalQueryNick` before calling.
//   * the synthetic kinds (`$server`, `$list`, `$home`, `$admin`) — not
//     identifiers at all. They are fold-invariant today, which is exactly why
//     folding them would be a silent no-op that stops being one the day a
//     synthetic name gains a capital.
const foldChannelKey = (sel: SelectedChannel): SelectedChannel =>
  sel !== null && sel.kind === "channel"
    ? { ...sel, channelName: canonicalChannel(sel.channelName) }
    : sel;

/**
 * Per-channel seed count pair: `messages` (content kinds) + `events`
 * (presence kinds). Hydrated from the server's join reply (`unread_count`
 * — total, not split) and the `/me` envelope (`unread_counts` — split).
 *
 * `unread_count` from the per-channel join reply is summed against
 * `messages` for simplicity: the join-time seed is a one-shot fallback
 * value, and the memo prefers local scrollback the moment any row for
 * that channel lands. The split-by-kind precision comes from `/me`
 * (bucket C) — until that lands, the join-reply seed treats every
 * unread row as a "messages" count which slightly overcounts the bold
 * badge on cold-start for never-opened channels. Acceptable interim
 * because cic loads scrollback the moment the operator focuses a
 * channel, at which point the local-derived count takes over.
 */
export type ServerSeedCount = { messages: number; events: number };

const exports = identityScopedStore((onIdentityChange) => {
  const [serverSeedCounts, setServerSeedCountsRaw] = createSignal<
    Record<ChannelKey, ServerSeedCount>
  >({});
  const [selectedChannel, setSelectedChannelRaw] = createSignal<SelectedChannel>(null);

  onIdentityChange(() => setServerSeedCountsRaw({}));
  onIdentityChange(() => setSelectedChannelRaw(null));

  // UX-5 bucket BU (2026-05-19): idempotent setter. Re-clicking an
  // already-active sidebar row passed a new object literal to the raw
  // setter, which made Solid's `===` equality compare by identity and
  // re-fired the on(selectedChannel) consumer in `mentions.ts` (a
  // standalone `on(selectedChannel)` effect that cleared mentionCounts
  // on every fire). The leave-arm below + ScrollbackPane's on(key, …)
  // already guarded on string-key equality and were safe; only the
  // mentions arm crossed the invariant. Operator perception: clicking
  // the open channel "did something" (red badge cleared without the
  // operator having read anything new). Post-BU the mentions clear is
  // consolidated into the focus arm (gated by the visibility +
  // selection arms in this file), AND the setter short-circuits at
  // its boundary so no observer sees a non-transition.
  //
  // Exact-tuple equality: any change in slug, name, or kind is a real
  // transition. null vs non-null is also a transition (covers logout +
  // identity reset paths).
  // #125 — back target for the $list directory overlay. The directory
  // is a transient pseudo-window with a close button; closing it should
  // restore whatever window was active when it opened, not blank the
  // pane. A single back pointer (NOT a history stack), captured only on
  // the non-list → list transition below so background selection churn
  // while browsing the directory can't clobber it. Reset on identity
  // rotation alongside the other identity-scoped state.
  let backTarget: SelectedChannel = null;
  onIdentityChange(() => {
    backTarget = null;
  });

  const setSelectedChannel = (input: SelectedChannel): void => {
    const next = foldChannelKey(input);
    const cur = untrack(selectedChannel);
    if (sameSelection(cur, next)) return;
    // Entering a transient overlay — the $list directory pane or the #188
    // mentions panel — from a real window: remember it so
    // closeToPreviousWindow restores the exact window that was focused
    // before the overlay opened (#125). Both panes reuse `.directory-close`
    // + closeToPreviousWindow, so they must record their opener the same
    // way. Overlay→overlay transitions never overwrite the remembered
    // opener.
    if (
      (next?.kind === "list" || next?.kind === "mentions") &&
      cur !== null &&
      cur.kind !== "list" &&
      cur.kind !== "mentions"
    ) {
      backTarget = cur;
    }
    setSelectedChannelRaw(next);
  };

  // #243 — true iff `next` is the window already selected. The exact
  // negation of the idempotent setter's short-circuit (both route through
  // `sameSelection`), so a re-tap detection can never drift from the
  // no-op-transition rule. Untracked: callers are event handlers (a
  // Sidebar / BottomBar tap), not reactive scopes — reading the selection
  // signal here must not subscribe them.
  //
  // #1396 — through `foldChannelKey` for the same reason the setter is: the
  // two must agree on what "the same window" means, and the setter now stores
  // the folded name. Skipping the fold here would answer "not a re-tap" for a
  // tap on the very window it just focused.
  const isActiveSelection = (next: SelectedChannel): boolean =>
    sameSelection(untrack(selectedChannel), foldChannelKey(next));

  // Resolve the window to focus when a window closes or a transient
  // overlay is dismissed: most-recently-used live channel/query (MRU) →
  // the network's server window if connected → home. Pure: reads current
  // store values and returns a target; the caller applies it. Shared by
  // the close-window picker (bucket E) and closeToPreviousWindow (#125)
  // so the fallback chain lives in one place.
  const resolveFallbackWindow = (
    excludeKey: ChannelKey | null,
    fallbackSlug: string,
  ): SelectedChannel => {
    const cbs = channelsBySlug() ?? {};
    const qwbn = queryWindowsByNetwork();
    const isLiveKey = (key: ChannelKey): boolean => {
      const decoded = decodeChannelKey(key);
      if (decoded === null) return false;
      const { slug, name } = decoded;
      const chans = cbs[slug] ?? [];
      if (chans.some((c) => c.name === name)) return true;
      const net = networkBySlug(slug);
      if (net) {
        const qs = qwbn[net.id] ?? [];
        const casemapping = casemappingForNetwork(net.id);
        if (qs.some((q) => nickEquals(q.targetNick, name, casemapping))) return true;
      }
      return false;
    };

    const next = pickLiveMru(excludeKey, isLiveKey);
    if (next !== null) {
      const decoded = decodeChannelKey(next);
      if (decoded !== null) {
        const { slug, name } = decoded;
        const chans = cbs[slug] ?? [];
        if (chans.some((c) => c.name === name)) {
          return { networkSlug: slug, channelName: name, kind: "channel" };
        }
        const net = networkBySlug(slug);
        if (net) {
          const qs = qwbn[net.id] ?? [];
          const casemapping = casemappingForNetwork(net.id);
          const match = qs.find((q) => nickEquals(q.targetNick, name, casemapping));
          if (match !== undefined) {
            return { networkSlug: slug, channelName: match.targetNick, kind: "query" };
          }
        }
      }
    }

    // No live MRU candidate. Fall back to the fallback network's server
    // window IF still connected, otherwise home.
    //
    // issue 2222 — the gate used to short-circuit on
    // `closedNet.kind === "visitor" ||`, under "visitor networks have no
    // connection_state — always assume connected". That is the #211 phase 6
    // premise, retired, sitting here as a live disjunct: a visitor's PARKED
    // network satisfied it and was treated as connected, so closing their last
    // window dropped focus into a server window instead of home.
    //
    // Fixing it is also what keeps the hiding rule coherent. A parked network
    // now leaves the sidebar for BOTH kinds (`isNetworkParked`), so the old
    // branch would have landed a visitor in a window whose network the sidebar
    // no longer draws — focus with no way back. The premise sentence is
    // deleted, not corrected.
    if (networkBySlug(fallbackSlug)?.connection_state === "connected") {
      return { networkSlug: fallbackSlug, channelName: SERVER_WINDOW_NAME, kind: "server" };
    }
    return { networkSlug: HOME_WINDOW_SLUG, channelName: HOME_WINDOW_NAME, kind: "home" };
  };

  // issue 2430 — an encrypted window is live while its chat is open in the
  // device's chat index (lib/encryptedChats.ts): its × closes the chat, and
  // the close-watcher below moves the focus as it does for a closed query.
  const encryptedWindowOpen = (sel: NonNullable<SelectedChannel>): boolean => {
    const peer = encryptedWindowPeer(sel.channelName);
    return peer !== null && encryptedChat(sel.networkSlug, peer)?.open === true;
  };

  // True if `sel` is still a window we can focus. Pseudo-windows
  // (home/admin/mentions) and a known server network are always
  // restorable; channel/query only while present in their live store.
  // null and the $list overlay itself are never restore targets.
  const selectionIsRestorable = (sel: SelectedChannel): boolean => {
    if (sel === null) return false;
    switch (sel.kind) {
      case "home":
      case "admin":
      case "mentions":
        return true;
      case "server":
        // A server window is restorable as long as its network still
        // exists — INTENTIONALLY not gated on connection_state (unlike
        // resolveFallbackWindow's server FALLBACK, which only fires for a
        // connected network). "Close returns to the previous window" is
        // more faithful than bouncing to home, and this is near-
        // unreachable anyway: bucket D redirects to home the moment a
        // network parks while its $list is focused (sel.networkSlug ===
        // the parking net). Keep the two predicates' divergence as-is.
        return networkBySlug(sel.networkSlug) !== undefined;
      case "channel":
      case "query": {
        const cbs = channelsBySlug() ?? {};
        const chans = cbs[sel.networkSlug] ?? [];
        if (chans.some((c) => c.name === sel.channelName)) return true;
        if (windowIsPresent(channelKey(sel.networkSlug, sel.channelName))) return true;
        const net = networkBySlug(sel.networkSlug);
        if (net) {
          const qs = queryWindowsByNetwork()[net.id] ?? [];
          const casemapping = casemappingForNetwork(net.id);
          if (qs.some((q) => nickEquals(q.targetNick, sel.channelName, casemapping))) return true;
        }
        return false;
      }
      case "encrypted":
        return encryptedWindowOpen(sel);
      case "list":
        return false;
    }
  };

  // #125 — close the $list directory overlay: restore the window that
  // was active when it opened if still focusable, otherwise fall through
  // the shared MRU → server → home chain.
  const closeToPreviousWindow = (fallbackSlug: string): void => {
    untrack(() => {
      const back = backTarget;
      backTarget = null;
      if (selectionIsRestorable(back)) {
        setSelectedChannel(back);
        return;
      }
      setSelectedChannel(resolveFallbackWindow(null, fallbackSlug));
    });
  };

  /**
   * Seeds the per-channel `{messages, events}` count. Called from
   * `subscribe.ts`'s `applyJoinReply` arm with `{messages:
   * unread_count, events: 0}` (the join reply doesn't split), and
   * from `readCursor.ts`'s `applyMeEnvelope` arm with the split shape
   * directly (bucket C). Replaces the existing key; the memo derives
   * from this map only when local scrollback is absent for the key.
   */
  const setServerSeedCount = (key: ChannelKey, seed: ServerSeedCount): void => {
    setServerSeedCountsRaw((prev) => {
      const existing = prev[key];
      if (existing && existing.messages === seed.messages && existing.events === seed.events) {
        return prev;
      }
      return { ...prev, [key]: seed };
    });
  };

  /**
   * Drop the seed for one window — the rows it counted are GONE.
   *
   * issue 2096. The seed is written by `/me` and by the join reply and by
   * nothing else, so a destructive
   * `DELETE /networks/:slug/archive/:target` left its count standing until the
   * next cold load. That was invisible while nothing rendered it: the archive
   * row disappears with the listing refresh, the sidebar draws no row for an
   * archived window, and `windowCandidates()` never enumerates one. The
   * launcher's rollup badge (`lib/archiveRollup.ts`) is the first surface that
   * sums seed keys with no window behind them, so the stale entry became a
   * number the operator could neither clear nor chase.
   *
   * Deliberately narrower than `readCursor`'s posture in the same handler,
   * which does NOT clear (see `userTopic.ts`): a cursor is cross-device state
   * the server owns, while this map is a local projection of counts whose rows
   * the server just deleted. Dropping it loses nothing another device needs.
   */
  const clearServerSeedCount = (key: ChannelKey): void => {
    setServerSeedCountsRaw((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _dropped, ...rest } = prev;
      return rest;
    });
  };

  /**
   * Bulk-hydrate the seed map from the `/me` envelope's
   * `unread_counts` nested map (`%{slug => %{chan => {messages,
   * events}}}`). Replaces the entire map — same cold-load semantic as
   * `applyMeEnvelope` in `readCursor.ts`. Bucket C wires the call
   * site.
   */
  const applySeedEnvelope = (envelope: Record<string, Record<string, ServerSeedCount>>): void => {
    const next: Record<ChannelKey, ServerSeedCount> = {};
    for (const [slug, perChannel] of Object.entries(envelope)) {
      for (const [chan, counts] of Object.entries(perChannel)) {
        if (counts && typeof counts.messages === "number" && typeof counts.events === "number") {
          next[channelKey(slug, chan)] = counts;
        }
      }
    }
    setServerSeedCountsRaw(next);
  };

  // Bucket C (2026-06-01) — wire `/me` `unread_counts` envelope into
  // `serverSeedCounts`. The hand-off lives here (not in networks.ts's
  // `/me` resource fetcher) because networks.ts ↔ selection.ts is a
  // circular import pair: a top-level `import { applySeedEnvelope }
  // from "./selection"` in networks.ts captures `undefined` under
  // vitest re-entry (selection.ts is mid-eval when networks.ts pulls
  // the binding). Reading `user()` reactively from this side is the
  // one-way arrow that avoids the cycle — selection.ts already imports
  // `networks`/`channelsBySlug`/`networkBySlug`, so adding `user` to
  // the same import doesn't grow the surface.
  //
  // The effect fires on every `user()` change: cold-load login, token
  // rotation, refetch after admin-mid-session demote. On the null
  // arms (logout, pre-login resource still resolving) the seed map
  // resets via the identity-rotation `onIdentityChange` arm above; no
  // explicit clear needed here.
  createEffect(
    on(user, (m) => {
      if (m == null) return;
      applySeedEnvelope(m.unread_counts ?? {});
    }),
  );

  // ---------------------------------------------------------------
  // Derived memos: messages/events/total unread per channel.
  //
  // For each known channel (union of `serverSeedCounts` keys + any
  // `scrollbackByChannel` keys), compute `{messages, events}` from
  //   * local scrollback rows with id > cursor split by isContentKind,
  //   * OR the seed value when local scrollback is empty for the key.
  //
  // The memos return plain `Record<ChannelKey, number>` to keep the
  // export shape byte-identical to the pre-refactor signals — every
  // consumer (Sidebar, BottomBar, Shell, focus-rule) continues to read
  // `messagesUnread()[key] ?? 0` without changes.
  // ---------------------------------------------------------------
  type Computed = { messages: number; events: number };

  const perChannelUnread = createMemo((): Record<ChannelKey, Computed> => {
    const sb = scrollbackByChannel();
    const cursors = readCursors();
    const seeds = serverSeedCounts();
    // #239 — the badge must count over VISIBLE rows only. `membersByChannel`
    // feeds the presence-filter member-count default; reading it here makes
    // the memo re-run when membership crosses the large-channel threshold, in
    // lock-step with `presenceRowVisible`'s pref-signal dependency below.
    const members = membersByChannel();
    // #576 — resolve the operator's per-network own nick to strip own-authored
    // CONTENT from the count (see the content-row loop below). Read reactively
    // so a NICK change / login re-runs the count. Per-key resolution uses
    // `networkBySlug(slug)` inside the loop (a nick is per-network).

    const result: Record<ChannelKey, Computed> = {};

    // Seed-only channels — the cold-start path where the operator
    // hasn't opened the channel yet so local scrollback is empty.
    // Keep the seed as the displayed count; once they focus and we
    // hydrate scrollback, the local-derived branch below takes over
    // automatically (and the cursor write that follows drops the
    // count to zero, dropping the key from the displayed map).
    for (const [rawKey, seed] of Object.entries(seeds)) {
      const key = rawKey as ChannelKey;
      result[key] = { messages: seed.messages, events: seed.events };
    }

    const farBehind = farBehindByChannel();
    // issue 2069 — the server's own answer for a window whose loaded unread
    // region is truncated (#947). Until now only the in-pane divider spent it,
    // so the pill fell back to counting the rows the store holds — one page
    // out of thousands after a jump, and then ZERO as the operator read
    // through that page. Same record, same unit, now both surfaces.
    const measured = measuredUnreadByChannel();

    // #2037 — a far-behind window reads its counts from the far-behind entry,
    // not from the seed. Both are `count_after_split/6` at the read cursor, so
    // this is not a change of definition; what it buys is that the bar and the
    // pill become the SAME VARIABLE rather than two values that happen to
    // agree. #2037 measured what "happen to agree" is worth: the bar was
    // rendering a raw row count against a split badge and nothing in the tree
    // could notice.
    //
    // It is also the fresher of the two. The seed is written by `/me` and by
    // the join reply and by nothing else — the per-message `window_counts`
    // push carries `messages`/`events` but cic ignores them (#239, they are
    // client-derived for the presence filter), and a far-behind key skips
    // client derivation by design. So the seed for such a window is frozen at
    // login; the probe was taken when the pane opened.
    for (const [rawKey, far] of Object.entries(farBehind)) {
      result[rawKey as ChannelKey] = { messages: far.missed, events: far.events };
    }

    // Locally-hydrated channels — count rows past the cursor by kind.
    // Override any seed entry: local truth wins because the seed is
    // a sync-time snapshot that may be stale by the time we render.
    for (const [rawKey, rows] of Object.entries(sb)) {
      const key = rawKey as ChannelKey;
      const decoded = decodeChannelKey(key);
      if (decoded === null) continue;
      // #693 — a far-behind window is the one case where local truth is the
      // WORSE truth. Its rows are the tail, deliberately disjoint from the
      // unread region, so counting them reports ~50 for a window that is
      // thousands behind — and the operator would see a small, ignorable
      // badge for the very state the change exists to surface. What stands in
      // its place is the far-behind entry written fifteen lines above, NOT the
      // seed — this `continue` protects that write, and keeps the
      // filter-and-count below off the largest windows in the store.
      //
      // 🔴 issue 2053 — this used to end "the seed is the honest number here,
      // so leave it standing", which #2037 had already made false. A window
      // caught up on at join carries a truthful ZERO seed and the ring cap can
      // arm far-behind from a purely local burst, so on that sentence the
      // badge says nothing at all — more certainly the more unread piles up.
      if (farBehind[key]) continue;
      const cursorMapKey = `${decoded.slug} ${decoded.name}`;
      const cursor = cursors[cursorMapKey] ?? 0;
      const memberCount = (members[key] ?? []).length;
      // #576 — the per-network own nick for this key's network. A nick is
      // per-network (`net.nick`), so resolve it per key; null when the
      // network isn't known yet (nickEquals is null-safe → no exclusion).
      // issue 2045 — built by the SHARED resolver, which is also what the
      // far-behind maintenance in `scrollback.ts` now calls. It used to be
      // assembled inline right here, and a second hand-built copy over there
      // would have been the very drift that issue is about: two spellings of
      // "who am I in this window" feeding two counts meant to be one number.
      // The per-network nick, the per-network casemapping and the #396
      // self-window carve-out all live in `unreadRowContextFor`.
      const ctx: UnreadRowContext = unreadRowContextFor(decoded.slug, decoded.name);
      // #239 — skip rows the presence filter hides for this channel: the pane
      // never renders them, so counting them would leave a badge the operator
      // can never clear by reading. Same predicate the pane's `rows()` filter
      // uses (reconcile-to-one, not a forked filter) — which is also why the
      // filter is applied HERE and not inside `unreadCount`: it is per-channel
      // UI state, and the shared module must not reach for it.
      const visible = rows.filter((row) => presenceRowVisible(key, memberCount, row));
      // issue 2069 — the MESSAGES bucket is `unreadMessagesAfter`, the one
      // function the in-pane divider also calls. No upper bound: the pill is
      // the LIVE answer, so an arrival past the frozen session top still
      // counts (the divider, which must not renumber under a reader, passes
      // its frozen top instead). The measurement is what stops the count
      // collapsing to the fetch page after a #693 jump.
      const msgs = unreadMessagesAfter(visible, cursor, null, measured[key], ctx);
      // The presence sibling, same operator-owned rule. Own JOIN/PART rows
      // used to land here — `subscribe.ts` drops them at its own gate, but
      // that gate has not reached the badge since the count became derived,
      // so a `/part → /join` cycle bumped the faint pill by two.
      let evts = 0;
      for (const row of visible) {
        if (row.id > cursor && countsAsUnreadEvent(row, ctx)) evts++;
      }
      result[key] = { messages: msgs, events: evts };
    }

    // #887 — the focused-window badge suppression is GONE (it used to sit
    // here as a final overwrite zeroing the selected-AND-visible window's
    // `{messages, events}`; 2026-06-02, decouple-unread-badge RC1).
    //
    // It drew "you are looking at this" and "you have read this" the same
    // way — as no badge — while deliberately NOT advancing the cursor. On a
    // window the operator was actively reading the badge therefore vanished
    // on select and came back WHOLE (1832) on leave, which reads as a broken
    // counter. Nothing was wrong; the display was lying by omission.
    //
    // The count is now the same question in every window, focused or not:
    // "how much have you not read". It falls where it should because the
    // cursor now advances WHILE the operator reads — ScrollbackPane's
    // read-at-the-tail arm marks what is on screen at the tail read, on top
    // of the pre-existing settle writers (scroll-settle, leave, blur, send).
    // The in-pane `── N unread ──` divider is unaffected: it reads the FROZEN
    // `markerCursorId` snapshot, not the live cursor (freeze contract).
    //
    // The one window whose count still cannot fall is a far-behind one
    // (#693 freezes its cursor on purpose). That is now SAID rather than
    // hidden — #888 marks the badge as a distance, not a stuck counter.
    //
    // #981 (2026-08-07) — a NARROWER suppression is back, and the paragraph
    // above is why it is allowed to be. DO NOT delete this for the #887
    // reason without re-reading both:
    //
    // #887's objection was that the suppression was DISHONEST — it hid the
    // badge without advancing the cursor, so the count vanished on select and
    // returned whole on leave. That objection no longer holds: the cursor
    // now advances WHILE the operator reads (the read-at-the-tail arm), so a
    // window suppressed here genuinely becomes read within the arm's settle
    // and the number does not come back. What is hidden is the 500 ms gap
    // between "the row landed" (this memo, synchronous) and "the arm marked
    // it read" (debounced) — the blink vjt reported as a badge flashing `1`
    // on a pane he was already at the bottom of.
    //
    // The predicate is NOT re-derived here. `readingAtTailKey` is the pane's
    // own read-at-the-tail gate — visible tab AND geometrically at the tail
    // AND rows rendered — published whole, so the badge is suppressed for
    // exactly the window the cursor arm is about to mark read. Reassembling
    // it from `selectedChannel` (an intent, not a geometry) or from
    // `followMode` would fork it, and the fork would be the #887 complaint
    // again the first time the two disagreed. A backgrounded tab publishes
    // `null` and keeps accruing.
    //
    // A far-behind window (#693) is deliberately excluded: its cursor is
    // frozen, the arm cannot advance it, and the rows the operator is at the
    // bottom of are NOT the unread region. Suppressing there would hide the
    // one count that can never come back on its own.
    const readingKey = readingAtTailKey();
    if (readingKey !== null && !farBehind[readingKey] && result[readingKey] !== undefined) {
      result[readingKey] = { messages: 0, events: 0 };
    }

    return result;
  });

  const messagesUnread = createMemo((): Record<ChannelKey, number> => {
    const out: Record<ChannelKey, number> = {};
    for (const [rawKey, c] of Object.entries(perChannelUnread())) {
      const key = rawKey as ChannelKey;
      if (c.messages > 0) out[key] = c.messages;
    }
    return out;
  });

  const eventsUnread = createMemo((): Record<ChannelKey, number> => {
    const out: Record<ChannelKey, number> = {};
    for (const [rawKey, c] of Object.entries(perChannelUnread())) {
      const key = rawKey as ChannelKey;
      if (c.events > 0) out[key] = c.events;
    }
    return out;
  });

  const unreadCounts = createMemo((): Record<ChannelKey, number> => {
    const out: Record<ChannelKey, number> = {};
    for (const [rawKey, c] of Object.entries(perChannelUnread())) {
      const key = rawKey as ChannelKey;
      const total = c.messages + c.events;
      if (total > 0) out[key] = total;
    }
    return out;
  });

  // UX-8 (b): scroll-settle cursor update — forward-only gate.
  // Reads the current cursor for (slug, name) via getReadCursor; POSTs
  // only when `candidateId` strictly exceeds it. Today's cursor is
  // monotonic (focus-leave + browser-blur always write the tail id);
  // this helper preserves that invariant when scroll-settle becomes
  // the third trigger. Server (Grappa.ReadCursor.set/4) supports
  // backward moves via last-write-wins, but cic does not exercise
  // them — kept as a single-source guard at the client boundary.
  //
  // Token guard: identity-rotation can null the bearer mid-effect.
  // #693 — the far-behind freeze. A pane that anchored at the tail renders
  // rows thousands of ids above the operator's real read position, and every
  // writer that funnels through here (scroll-settle, the visibility-hide arm,
  // the scroll-to-bottom gesture) offers the newest RENDERED id. Forward-only
  // would happily take it and mark the entire abandoned region read —
  // server-side, fanned to every other device, destroying the position the
  // jump affordance exists to return to. Freeze the cursor for as long as the
  // window is far behind; `dismissFarBehind` (scrollback.ts) is the one
  // deliberate way past it.
  //
  // #1019 — focus-leave USED to be a fourth name on that list and is not
  // anymore, and the difference is who decides, not how strong the guard is.
  // Leaving a window is a deliberate "I'm done with those", so the leave arm
  // (ScrollbackPane's `on(key, prevKey)` effect) now routes a far-behind
  // window to `dismissFarBehind` and never reaches this door with it. The
  // door itself did NOT move: the remaining three are passive — scrolling,
  // backgrounding, tapping to the bottom — and each still gets frozen out
  // here. Do not "simplify" that routing back into a caller-kind flag on this
  // function: the freeze is the invariant, and a parameter that can turn it
  // off is one careless call site away from being gone for all four.
  //
  // This is the single door for cursor advancement, so the guard belongs
  // here, not in each of the four callers.
  const setCursorIfAdvances = (
    networkSlug: string,
    channelName: string,
    candidateId: number,
  ): void => {
    if (farBehindByChannel()[channelKey(networkSlug, channelName)]) return;
    const current = getReadCursor(networkSlug, channelName);
    if (current !== null && candidateId <= current) return;
    const bearer = untrack(token);
    if (!bearer) return;
    void setReadCursor(bearer, networkSlug, channelName, candidateId);
  };

  createEffect(
    on(selectedChannel, (sel) => {
      // BUGHUNT-2: focus-leave cursor write moved to ScrollbackPane's
      // `on(key, …)` effect + `onCleanup` — the pane owns its DOM
      // geometry and writes the honest `lastFullyVisibleRowId`, not
      // the store-tail. This effect retains the orthogonal arms:
      // MRU-record, scrollback hydrate.
      //
      // #267 — mention focus-clear is GONE from here: `mentionCounts` is
      // now server-authoritative (mentions.ts) with a focus-zero OVERLAY
      // in its memo (selected+visible window renders 0), so no explicit
      // per-focus clear is needed. The message/event memos likewise drop
      // automatically as the cursor advances.

      if (!sel) return;
      // UX-4 bucket E: record channel/query focus into MRU. Only
      // channel + query enter MRU — home is the final fallback target
      // (recording it would make it the default next-pick and short-
      // circuit the chain). Server windows are the second-tier fallback
      // (skipped for the same reason). list / mentions are ephemeral
      // and shouldn't take focus when an unrelated window closes.
      if (sel.kind === "channel" || sel.kind === "query") {
        recordFocus(channelKey(sel.networkSlug, sel.channelName));
      }
      // Issue #35 — persist the focused window per identity, so a
      // PWA reload / browser restart lands the operator back on the
      // last viewed channel instead of the cold-load `$home` default.
      // Only restorable kinds (channel / query / server) are saved;
      // `home` is the existing fallback target, `admin` is gated on
      // is_admin (and would redirect home on demote anyway),
      // `mentions` / `list` are ephemeral surfaces. The restorable set
      // is exactly the scrollback-backed set — both reduce to "has a
      // real (network, channel) identity" — so it shares the
      // `kindHasScrollback` predicate. If a future kind ever needs to
      // be scrollback-backed but NOT restorable (or vice versa), split
      // this back into its own predicate rather than letting the two
      // silently diverge.
      const me = untrack(user);
      if (me && kindHasScrollback(sel.kind)) {
        saveLastFocused(me.id, {
          networkSlug: sel.networkSlug,
          channelName: sel.channelName,
          kind: sel.kind,
        });
      }
      // Fire-and-forget: the verb guards itself via scrollback's
      // loadedChannels Set. Gated on `kindHasScrollback` (grappa-irc#81):
      // synthetic windows — `$home` (status buffer), `$admin` (console),
      // and the empty-channelName `mentions` aggregate — have no
      // server-backed scrollback channel, so a `/messages` GET for them
      // 404s and trips the production fail2ban http-404 ban cascade.
      // Only channel / query / server map to a real scrollback channel.
      if (kindHasScrollback(sel.kind)) {
        // #159 item 1 — ACTIVATION freshness, but ONLY for a RE-SELECT of an
        // ALREADY-LOADED window. Capture the load-once bit BEFORE calling
        // `loadInitialScrollback`: that verb adds the key to `loadedChannels`
        // synchronously, so reading `wasLoaded` after would always be `true`.
        const wasAlreadyLoaded = wasLoaded(sel.networkSlug, sel.channelName);
        void loadInitialScrollback(sel.networkSlug, sel.channelName);
        // Re-selecting an already-loaded tab is the ONLY case the load-once
        // `loadInitialScrollback` cannot cover: it fetches nothing, so a
        // live-delivery gap that opened while the tab was backgrounded
        // (socket stayed open, this one channel stopped receiving) would stay
        // invisible until a full reload. `refreshScrollback` is the catch-up
        // verb (`?after=<resume-cursor>`, id-deduped, capped 200,
        // frozen-divider-safe), idempotent + per-key in-flight-guarded.
        //
        // A FRESH OPEN must NOT fire it (#159 regression, cp13-s5). That path
        // is already covered by `loadInitialScrollback` + the live
        // per-channel WS subscription + the query-window join-ok
        // `refreshScrollback`. Firing the activation refetch on a just-opened
        // window STARVES that join-ok safety net: on `/msg <ghost>`, the
        // early activation refetch grabs `refreshScrollback`'s per-key
        // in-flight lock and returns `[]` (it fires before the server
        // persists the 401 ERR_NOSUCHNICK); the join-ok `refreshScrollback` —
        // the REST backfill that catches the 401 whenever the live push is
        // missed during subscription settling — then finds the lock held and
        // returns early WITHOUT fetching. With both the live push and the
        // safety-net refetch lost, the 401 notice never renders. Gating on
        // "already loaded before this activation" removes the fresh-open fire
        // and frees the lock for the join-ok refetch. Same `kindHasScrollback`
        // gate: synthetic windows have no /messages channel and a GET would
        // 404 into the fail2ban cascade.
        if (wasAlreadyLoaded) {
          void refreshScrollback(sel.networkSlug, sel.channelName);
        }
      }
    }),
  );

  // #267 — the browser-visibility mention-clear arm is GONE. mentionCounts
  // (mentions.ts) reads `isDocumentVisible` in its own focus-zero overlay
  // memo, so a focus-regain re-renders the selected window's badge at 0
  // without an explicit clear here. The TRUE→FALSE (browser blur)
  // cursor-write already moved to ScrollbackPane's own visibility effect
  // (BUGHUNT-2). This effect had no other responsibility, so it's removed
  // rather than left as an empty shell.

  // UX-4 bucket D — redirect selection to home when a network the
  // user is currently looking at transitions INTO `:parked` or
  // `:failed`. Subscribing here (one place, on `networks()`) means
  // the redirect fires uniformly across:
  //   * Server-window × button (`disconnectNetwork` in windowClose.ts)
  //   * /disconnect typed in the compose box
  //   * Server-side circuit-breaker park (admission control trips)
  //   * Operator `bin/grappa disconnect` admin verb
  // Per CLAUDE.md "Don't duplicate state — derive it": one effect,
  // one transition observer, all triggers route through it.
  //
  // Transition-only: `lastConnectionState` tracks the previous value
  // per network slug so the operator can still navigate BACK to a
  // parked window (to view history) without bouncing back to home.
  // Identity rotation clears the map so a re-login doesn't carry
  // stale state from the previous identity's networks.
  //
  // The HOME window has no network credential, so `networkBySlug` returns
  // undefined → no entry in the map → no redirect (correct: home is the
  // redirect TARGET, never the source). This used to say "home and visitor
  // windows"; issue 2222 struck the visitor half — a visitor DOES have a
  // credential row, carries a real `connection_state`, and is redirected
  // exactly like a user.
  // issue 2059 — TWO FEEDS, ONE OBSERVER, ONE MEMORY.
  //
  // The Map below is the only record of "what state was this network in last
  // time we heard". Both feeds write it through `observeConnectionState`, so
  // there is no second structure to keep in step (CLAUDE.md: don't duplicate
  // state — derive it).
  //
  //   FEED 1, the `networks()` sample (below). Load-bearing and NOT
  //   removable: Phoenix PubSub does not replay, so after a WS gap the
  //   refetch is the ONLY evidence that a park happened while we were
  //   deaf.
  //
  //   FEED 2, the `connection_state_changed` event (`noteConnectionState`,
  //   called from userTopic.ts). Load-bearing for the opposite reason: a
  //   sample cannot see a transition shorter than the interval between
  //   samples. `/reconnect` parks and reconnects immediately, and a GET
  //   issued at the park leg can be answered after the credential already
  //   reads `connected` again — so the sampled sequence is
  //   ["connected", "connected"], `curr === prev`, and the redirect is
  //   silently lost. That is issue 2059, and it is why
  //   `issue1796-reconnect-bounces-network.spec.ts` went red 17 times and
  //   was mistaken for a flake: the spec is honest, the product loses the
  //   park.
  //
  // The event carries `to` directly, so it cannot be outrun by its own
  // refetch. This is the same move `userTopic.ts` already makes three lines
  // above that refetch — `patchHomeNetwork(payload.network)`, added by REV-J
  // M15 to close "the temporal window where Sidebar saw the new state but
  // HomePane hadn't yet". Bucket D was the last consumer still sampling.
  const lastConnectionState = new Map<string, string>();
  onIdentityChange(() => lastConnectionState.clear());

  // The single transition test. `prev === undefined` (first sighting of a
  // slug) is deliberately NOT a transition: the operator may open the app
  // with a network already parked, and bouncing them off a window they chose
  // to look at is not a redirect, it is a hijack.
  const observeConnectionState = (slug: string, curr: string): void => {
    const prev = lastConnectionState.get(slug);
    lastConnectionState.set(slug, curr);
    if (prev === undefined) return;
    if (curr === prev) return;
    if (curr !== "parked" && curr !== "failed") return;
    untrack(() => {
      const sel = selectedChannel();
      if (!sel) return;
      if (sel.networkSlug !== slug) return;
      setSelectedChannel({
        networkSlug: HOME_WINDOW_SLUG,
        channelName: HOME_WINDOW_NAME,
        kind: "home",
      });
    });
  };

  /**
   * FEED 2 — a `connection_state_changed` event, straight from the wire.
   *
   * Called by `userTopic.ts` with `payload.network_slug` / `payload.to`
   * BEFORE the refetch it also triggers, so a park shorter than one
   * round-trip is still observed. Writes the same Map the sample does, so
   * the sample that lands afterwards sees `curr === prev` and does not
   * redirect a second time.
   */
  const noteConnectionState = (slug: string, to: string): void => {
    observeConnectionState(slug, to);
  };

  createEffect(() => {
    const nets = networks();
    if (!nets) return;
    // Prune entries for slugs no longer in the live list (a DELETE
    // /networks unbinds a slug — without this the Map would carry
    // dead keys for the lifetime of the identity).
    const live = new Set(nets.map((n) => n.slug));
    for (const slug of lastConnectionState.keys()) {
      if (!live.has(slug)) lastConnectionState.delete(slug);
    }
    // issue 2222 — no `kind` narrow. This loop used to skip visitor rows, and
    // the asymmetry that created was the worst shape available: FEED 2 never
    // narrowed, so a visitor's redirect worked while the socket was up and
    // silently lost exactly the case FEED 1 exists for — a park that happened
    // while we were deaf, which the refetch is the only evidence of. Both
    // network shapes have declared `connection_state` non-optional since #211
    // phase 6, so there was never a field to protect.
    for (const net of nets) {
      observeConnectionState(net.slug, net.connection_state);
    }
  });

  // UX-4 bucket E — close-window auto-focus picker.
  //
  // Fires when the currently-selected window vanishes from its live
  // store (channelsBySlug drops the channel after a PART/kick/server-
  // side close; queryWindowsByNetwork drops the query after a
  // close_query_window broadcast). The picker shifts focus to:
  //
  //   1. The most-recently-viewed live channel/query window (MRU).
  //   2. The closed window's network server window, IF that network
  //      is still :connected (parked/failed networks would re-trigger
  //      bucket D's home-redirect — pre-empt the fight by routing
  //      straight to home).
  //   3. Home as the universal last resort.
  //
  // One reactive effect covers ALL close triggers — × button, /part
  // typed in compose, server-side kick, /disconnect cascade, query
  // close_query_window. Per CLAUDE.md "Don't duplicate state — derive
  // it": one observer, all triggers funnel through it.
  //
  // The just-closed key is evicted from MRU BEFORE the picker runs —
  // per `feedback_target_window_ux_rule` ("SOURCE state must clear at
  // switch BEFORE TARGET decisions"). Without eviction, pickLiveMru
  // would see the just-closed window at MRU head and short-circuit to
  // it (which by definition is no longer live, but the predicate would
  // need to know that — eviction is simpler than encoding the
  // exclude in the predicate).
  //
  // Race with bucket D: when /disconnect parks a network, the server
  // emits both per-channel PART (closes channels → bucket E fires) AND
  // a network-state flip to :parked (bucket D's home-redirect fires).
  // Both terminate at home; no infinite loop. Bucket E's server-window
  // fallback guard (`connection_state !== "connected"`) ensures bucket
  // E doesn't pick the just-parked network's server window only to
  // have bucket D bounce it to home on the next tick.
  //
  // Ordering note: if channelsBySlug refetches BEFORE the networks()
  // flip lands, bucket E sees `connection_state === "connected"` and
  // picks the parking network's server window momentarily; then bucket
  // D fires on the parked-transition and bounces to home. A brief
  // server-pane flash is visible. If networks() flips first, bucket D
  // fires (sel matches the parking net) → home; bucket E then early-
  // returns because sel.kind === "home". Both paths converge at home;
  // the flash is the cost of decoupling the two effects via the
  // signal-driven `connection_state` guard rather than peeking at
  // bucket D's `lastConnectionState` Map.
  //
  // Transition-only firing: the picker MUST NOT fire just because the
  // operator selected a window that isn't yet in channelsBySlug (e.g.
  // optimistic pending channels via the windowState branch). The effect
  // tracks `wasLive`: the previous-run result of the stillLive check
  // for the current selection key. Only when wasLive=true AND now
  // stillLive=false does the picker fire — that's the "window vanished"
  // transition. A fresh selection that starts not-live keeps wasLive
  // at false; the picker waits until the channel arrives in
  // channelsBySlug (then wasLive=true) before becoming armed.
  const lastSeenLive = new Map<ChannelKey, boolean>();
  onIdentityChange(() => lastSeenLive.clear());

  createEffect(() => {
    const sel = selectedChannel();
    if (!sel) return;
    if (sel.kind !== "channel" && sel.kind !== "query" && sel.kind !== "encrypted") return;

    const cbs = channelsBySlug() ?? {};
    const qwbn = queryWindowsByNetwork();
    const selKey = channelKey(sel.networkSlug, sel.channelName);

    const stillLive = (() => {
      if (sel.kind === "encrypted") return encryptedWindowOpen(sel);
      if (sel.kind === "channel") {
        const list = cbs[sel.networkSlug] ?? [];
        // Decode the canonical name via selKey rather than reusing
        // sel.channelName raw — bucket A canonicalises channel names
        // end-to-end, but a stray non-canonical setSelectedChannel
        // would otherwise stale-fire this check. Defensive parity with
        // the symmetric MRU lookup at the picker step (decoded from
        // a canonicalised ChannelKey).
        const decoded = decodeChannelKey(selKey);
        const name = decoded?.name ?? sel.channelName;
        if (list.some((c) => c.name === name)) return true;
        // UX-7-E: non-joined window states (pending|failed|kicked)
        // keep the row in the sidebar via pseudoChannelsForNetwork.
        // Selection must mirror that membership predicate so peer KICK
        // or JOIN-failed doesn't yank focus away before the operator
        // sees the reason / kick metadata in the (greyed) compose box.
        return windowIsPresent(selKey);
      }
      const net = networkBySlug(sel.networkSlug);
      if (!net) return false;
      const queries = qwbn[net.id] ?? [];
      const casemapping = casemappingForNetwork(net.id);
      return queries.some((q) => nickEquals(q.targetNick, sel.channelName, casemapping));
    })();

    const wasLive = lastSeenLive.get(selKey) ?? false;
    lastSeenLive.set(selKey, stillLive);

    if (stillLive) return;
    if (!wasLive) return;

    // Selection's window WAS live and now is not — transition fired.
    // Evict from MRU and pick the next focus through the shared fallback
    // chain (MRU → the closed network's server window if connected →
    // home). The visitor-network "always connected" rule + home last
    // resort live in resolveFallbackWindow.
    untrack(() => {
      evictFromMru(selKey);
      lastSeenLive.delete(selKey);
      setSelectedChannel(resolveFallbackWindow(selKey, sel.networkSlug));
    });
  });

  // #359 — jump to the status/server window (irssi's window 0). The status
  // window IS an ordinary selection target ({slug, $server, kind: "server"});
  // what it is NOT is part of the Alt+1..9 index space, which walks
  // channels/queries only — hence a verb of its own rather than an index.
  // WHICH network's status window is DERIVED, never stored: the selection's
  // own network when it has one (channel/query/server/list all carry a real
  // slug), else the first network in the sidebar's order. The identity-scoped
  // home/admin windows carry a `$`-sentinel slug that `networkBySlug` does not
  // know, which is exactly the "no network in focus" signal. No-op when no
  // network is bound at all.
  const selectStatusWindow = (): void => {
    untrack(() => {
      const sel = selectedChannel();
      const inFocus =
        sel !== null && networkBySlug(sel.networkSlug) !== undefined ? sel.networkSlug : undefined;
      const slug = inFocus ?? (networks() ?? [])[0]?.slug;
      if (slug === undefined) return;
      setSelectedChannel({
        networkSlug: slug,
        channelName: SERVER_WINDOW_NAME,
        kind: "server",
      });
    });
  };

  return {
    unreadCounts,
    messagesUnread,
    eventsUnread,
    serverSeedCounts,
    selectedChannel,
    setSelectedChannel,
    isActiveSelection,
    closeToPreviousWindow,
    selectStatusWindow,
    setServerSeedCount,
    clearServerSeedCount,
    applySeedEnvelope,
    setCursorIfAdvances,
    noteConnectionState,
  };
});

export const unreadCounts = exports.unreadCounts;
export const messagesUnread = exports.messagesUnread;
export const eventsUnread = exports.eventsUnread;
export const serverSeedCounts = exports.serverSeedCounts;
export const selectedChannel = exports.selectedChannel;
export const setSelectedChannel = exports.setSelectedChannel;
export const isActiveSelection = exports.isActiveSelection;
export const closeToPreviousWindow = exports.closeToPreviousWindow;
export const selectStatusWindow = exports.selectStatusWindow;
export const setServerSeedCount = exports.setServerSeedCount;
export const clearServerSeedCount = exports.clearServerSeedCount;
export const applySeedEnvelope = exports.applySeedEnvelope;
export const setCursorIfAdvances = exports.setCursorIfAdvances;
export const noteConnectionState = exports.noteConnectionState;
